/* eslint-disable camelcase */
import React, { useState, useRef, useEffect, useContext } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';
import PropTypes from 'prop-types';
import get from 'lodash/get';

// Components
import { CommentsContext } from 'components/Comments';
import CommentEditActionsMenu from 'components/Comments/CommentEditActionsMenu';
import ResponseForm from 'components/Comments/CommentForm/ResponseForm';
import ModeratorActions from 'components/Comments/CommentActions/ModeratorActions';

// Hooks
import useDisableSwiping from 'hooks/useDisableSwiping';

// Props
import CommentProps from 'components/Comments/Comment/CommentProps';

// Queries
import { COMMENT_FIELDS, GET_USER_COMMENT_RESPONSES, SAVE_RESPONSE } from '../CommentQueries';

// assets
import CssCommentActions from './CommentActions.scss';

function CommentActions(props) {
  const {
    canRespond,
    className,
    comment,
    hideQuestionText,
    onEdit,
    onResponseFormOpen,
    onResponseFormClose,
    hasPrimaryImage,
    responding,
    withModeratorControls,
  } = props;
  const { id, type, content_id } = comment;
  useStyles(CssCommentActions);
  const { asAuthor } = useContext(CommentsContext);
  const formCloseTimerRef = useRef(null);
  const textFieldRef = useRef(null);
  useDisableSwiping(textFieldRef);
  const [responseFormOpen, setResponseFormOpen] = useState(responding);
  const [active, setActive] = useState(responding);
  const [moderatorMode, setModeratorMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [focused, setFocused] = useState(responding);
  const [saveError, setSaveError] = useState(null);

  const loggedIn = useSelector(state => state.user.logged_in);
  const userId = useSelector(state => state.user.id);

  // Cleanup
  useEffect(
    () => () => {
      clearTimeout(formCloseTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (textFieldRef.current && focused) {
      textFieldRef.current.focus();
    }
  });

  const { data: userCommentData } = useQuery(GET_USER_COMMENT_RESPONSES, {
    variables: {
      content_id,
      _authenticated: true,
    },
    ssr: false,
    skip: !loggedIn,
    fetchPolicy: 'cache-first', // This result should be cached from the top level Comments component
    onError: err => {
      console.error('Failed to load user comments', err);
    },
  });

  const [saveComments, { loading: saveInProgress }] = useMutation(SAVE_RESPONSE, {
    onCompleted() {
      setDraft(null);
      setResponseFormOpen(false);
      setModeratorMode(false);
    },
    onError: err => {
      console.error('Failed to save comment', err);
      const errorCode = get(err, 'graphQLErrors[0].extensions.code');
      const niceErrorCode = errorCode ? ` (code: ${errorCode})` : '';
      const niceSaveError =
        get(err, 'graphQLErrors[0].message') ||
        `We can't save this right now. Please try again later${niceErrorCode}`;
      setSaveError(niceSaveError);
    },
    refetchQueries: ['GetAllRecipeComments'],
    update(cache, { data: updatedData }) {
      const { UserComment: updatedComment } = updatedData;
      // If the query results don't include this Comment then update the query
      if (
        !userCommentData.UserComments.find(
          existingComment => existingComment.id === updatedComment.id,
        )
      ) {
        cache.writeQuery({
          query: GET_USER_COMMENT_RESPONSES,
          variables: {
            content_id,
            _authenticated: true,
          },
          data: {
            UserComments: [...userCommentData.UserComments, updatedComment],
          },
        });
        // Update the responses field of the parent comment
        const updatedId = `Comment:${updatedComment.id}`;
        cache.updateFragment(
          {
            fragment: gql`
              fragment CommentResponses on Comment {
                responses(offset: 0) {
                  ...CommentFields
                }
              }
              ${COMMENT_FIELDS}
            `,
            fragmentName: 'CommentResponses',
            id: `Comment:${updatedComment.parent_id}`,
            variables: {},
          },
          data => {
            if (data) {
              return {
                ...data,
                responses: [
                  updatedComment,
                  ...data.responses.filter(existing => cache.identify(existing) !== updatedId),
                ],
              };
            }
            return {
              responses: [updatedComment],
            };
          },
        );
      }
    },
  });

  const handleReplyClick = event => {
    event.stopPropagation();
    setSaveError(null);
    setActive(true);
    if (moderatorMode) {
      setModeratorMode(false);
      setResponseFormOpen(true);
      onResponseFormOpen();
    } else {
      setResponseFormOpen(!responseFormOpen);
      if (!responseFormOpen) {
        onResponseFormOpen();
      } else {
        onResponseFormClose();
      }
    }
  };

  const handleModeratorRespondClick = event => {
    event.stopPropagation();
    setSaveError(null);
    setResponseFormOpen(true);
    setActive(true);
    setModeratorMode(true);
  };

  const handleFormClose = () => {
    // Form requests to close
    setResponseFormOpen(false);
    onResponseFormClose();
  };

  const handleFormClosed = () => {
    // Form finished closing
    // We want to make sure the form is hidden before we reset its state.
    formCloseTimerRef.current = setTimeout(() => {
      setDraft(null);
      setModeratorMode(false);
      setActive(false);
    }, 0);
  };

  const handleFormChange = values => {
    setDraft(values.response);
  };

  const handleFormSubmit = values => {
    saveComments({
      variables: {
        content_id,
        type: 'response',
        raw_text: values.response,
        parent_id: id,
        as_ckbk: moderatorMode,
        is_new: responseFormOpen,
      },
    });
  };

  const UserComments = get(userCommentData, 'UserComments') || [];
  const existingResponse = UserComments.find(
    _comment =>
      _comment.type === 'response' &&
      _comment.parent_id === id &&
      Boolean(_comment.as_ckbk) === false,
  );
  const existingCkbkResponse = UserComments.find(
    _comment =>
      _comment.type === 'response' &&
      _comment.parent_id === id &&
      Boolean(_comment.as_ckbk) === true,
  );
  let editOptionsMenu;
  if (moderatorMode && existingCkbkResponse) {
    editOptionsMenu = (
      <CommentEditActionsMenu
        contentId={content_id}
        commentId={existingCkbkResponse.id}
        type="response"
        onDeleteError={setSaveError}
        onDeleteCompleted={handleFormClose}
      />
    );
  } else if (existingResponse) {
    editOptionsMenu = (
      <CommentEditActionsMenu
        contentId={content_id}
        commentId={existingResponse.id}
        type="response"
        onDeleteError={setSaveError}
        onDeleteCompleted={handleFormClose}
      />
    );
  }

  if (!loggedIn) {
    return null;
  }

  let replyButton = null;
  if (comment.user.id === userId && comment.type === 'response') {
    replyButton = (
      <button className="link link--energized comment-actions__button" onClick={onEdit}>
        Edit
      </button>
    );
  } else if (canRespond) {
    replyButton = (
      <button className="link link--energized comment-actions__button" onClick={handleReplyClick}>
        {asAuthor ? 'Reply as author' : 'Reply'}
      </button>
    );
  } else if (comment.user.id === userId && type === 'question') {
    if (!hideQuestionText) {
      replyButton = (
        <span className="comment-actions__button comment-actions__faint">
          Other users can respond to your question
        </span>
      );
    }
  }

  return (
    <div className={`comment-actions ${replyButton ? className : null}`}>
      <div className={`comment-actions__row ${canRespond ? 'comment-actions__last-button' : null}`}>
        {active ? null : <div className="comment-actions__group">{replyButton}</div>}
        {withModeratorControls || (active && moderatorMode) ? (
          <ModeratorActions
            comment={comment}
            onEditClick={onEdit}
            hasPrimaryImage={hasPrimaryImage}
            onRespondClick={handleModeratorRespondClick}
          />
        ) : null}
      </div>
      {active ? (
        <ResponseForm
          respondAsCkbk={moderatorMode}
          focused={focused}
          id={`response-text-${id}`}
          isOpen={responseFormOpen}
          saveButtonText="Post response"
          textFieldLabel="Your response"
          onClose={handleFormClose}
          onClosed={handleFormClosed}
          onChange={handleFormChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onSubmit={handleFormSubmit}
          optionsMenu={editOptionsMenu}
          ref={textFieldRef}
          response={typeof draft === 'string' ? draft : ''}
          saveError={saveError}
          saveInProgress={saveInProgress}
        />
      ) : null}
    </div>
  );
}

CommentActions.propTypes = {
  canRespond: PropTypes.bool,
  hasPrimaryImage: PropTypes.bool,
  className: PropTypes.string,
  comment: CommentProps.isRequired,
  hideQuestionText: PropTypes.bool,
  onEdit: PropTypes.func,
  onResponseFormClose: PropTypes.func,
  onResponseFormOpen: PropTypes.func,
  responding: PropTypes.bool,
  withModeratorControls: PropTypes.bool,
};

CommentActions.defaultProps = {
  canRespond: false,
  hasPrimaryImage: false,
  className: '',
  hideQuestionText: false,
  onEdit: null,
  onResponseFormClose: () => {},
  onResponseFormOpen: () => {},
  responding: false,
  withModeratorControls: false,
};

CommentActions.whyDidYouRender = false;

export default React.memo(CommentActions);
